/**
 * @file MlrRichTextProvider.tsx
 * @brief Provider that tracks the focused MLR TipTap editor for the toolbar.
 *
 * @details
 * Filename is distinct from `mlrRichTextContext.ts` so Vite on macOS cannot
 * resolve `MlrRichTextProvider` to the hook module (08-09-2026).
 *
 * @author DES
 * @date 08-09-2026
 */

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Editor } from "@tiptap/react";
import {
  MlrRichTextContext,
  type MlrRichTextContextValue,
} from "./mlrRichTextContext";

export function MlrRichTextProvider({ children }: { children: ReactNode }) {
  const [editor, setEditor] = useState<Editor | null>(null);
  const editorRef = useRef<Editor | null>(null);

  const setFocusedEditor = useCallback((next: Editor | null) => {
    editorRef.current = next;
    setEditor(next);
  }, []);

  const clearIfFocused = useCallback((instance: Editor | null) => {
    if (!instance || editorRef.current !== instance) return;
    editorRef.current = null;
    setEditor(null);
  }, []);

  const value = useMemo<MlrRichTextContextValue>(
    () => ({ editor, setFocusedEditor, clearIfFocused }),
    [editor, setFocusedEditor, clearIfFocused],
  );
  return (
    <MlrRichTextContext.Provider value={value}>
      {children}
    </MlrRichTextContext.Provider>
  );
}
